/**
 * De Vergarde's specifics. THE ONLY FILE IN THE REPOSITORY THAT MAY HOLD THEM.
 * RETIRE-WITH-PRICECALCULATOR.
 *
 * ---------------------------------------------------------------------------
 * WHY ONE FILE, AND WHY IT IS NOT FOR REUSE
 * ---------------------------------------------------------------------------
 *
 * Everywhere else — including everywhere else in `apps/compat-2027/` — the code
 * is about the HolidayMedia CMS and the old calculator's wire format, both of
 * which are park-independent. Only this file knows that the park is De Vergarde,
 * that it sells camping pitches and rental accommodation, and where its website
 * lives.
 *
 * This is not a configuration system and must not grow into one. It is a
 * DELETION BOUNDARY: when the old site goes, `rm -rf apps/compat-2027/` takes
 * every park-specific constant in the codebase with it, and there is no second
 * place to check. A park-specific constant anywhere else in this app is a bug
 * even though nothing enforces it, because it survives the `rm`.
 *
 * The corollary is the useful one: pointing the bridge at HolidayMedia's test
 * site is ONE LINE here, and nothing else in the repository moves.
 */

/**
 * Where WE are served. The old `widget.js` hard-codes this as both its
 * `targetOrigin` and the value it compares `event.origin` against, so it cannot
 * be changed without HolidayMedia editing their pages — which is the whole
 * reason the cutover is a DNS re-point rather than a coordination exercise.
 */
export const CHILD_ORIGIN = 'https://prijs.devergarde.nl';

/**
 * Parent origins we accept messages from and post messages to.
 *
 * TWO REASONS THIS IS A LIST rather than the one production origin.
 *
 * First, `objs` tells us the parent's origin, but we have to send `list` BEFORE
 * we have received `objs` — the parent posts its first `objs` at its own
 * `DOMContentLoaded`, to `iframe.contentWindow`, which at that moment is still
 * `about:blank`, so the first one is always lost. `list` therefore has to go to
 * a `targetOrigin` we know in advance, and `'*'` would post the message to
 * whatever site had framed us.
 *
 * Second, the site is reachable under more than one name, and each is a
 * different origin. This list was NOT guessed: it is the host half of
 * `ORIGIN_URL` in the calculator this replaces, so it is the set that has been
 * framing the live calculator for years. The `.com` names are the mirror
 * HolidayMedia serve, which is what a test page is served from.
 *
 * The `prijs.*` names are deliberately absent — those are OUR origins, the ones
 * the calculator is served FROM. A page cannot frame itself into this list.
 *
 * A missing origin here is silent: the message is posted to a target that does
 * not match, the browser drops it, and the tiles simply never appear.
 */
export const HOST_ORIGINS: readonly string[] = [
  'https://www.devergarde.nl',
  'https://devergarde.nl',
  'https://www.vergarde.nl',
  'https://vergarde.nl',
  'https://www.devergarde.com',
  'https://devergarde.com',
];

/** Where `/` sends a visitor who reaches the calculator's own hostname. */
export const SITE_URL = 'https://www.devergarde.nl/';

/**
 * The two calculators the live site embeds, and how each is framed.
 *
 * Both are the same 2027 widget over a different part of the estate; what
 * differs is only what the host page has already committed to in CSS.
 *
 *   kamperen  ONE tariff card for a pitch category, framed directly by
 *             `/kampeer-tarieven-2026` at min-height 600. No tiles on that page
 *             and no `{[…]}` markers at all, so nothing to answer about: the
 *             card draws itself.
 *   verhuur   the rental search, injected by `widget.js` at min-height 490 into
 *             `/accommodatie-zoeken-2026`, whose thirteen tiles ARE marked. This
 *             is the one that needs `postMessageResults`.
 */
export interface Calculator {
  /** The `config` path, as it appears in the embed URL. */
  readonly config: string;
  /** Whether the host page carries marked tiles for us to answer about. */
  readonly kind: 'tariff-card' | 'tile-search';
  /** What the host page's inline style already reserves, in pixels. */
  readonly minHeight: number;
}

export const CALCULATORS: readonly Calculator[] = [
  { config: '2026/kamperen/comfort', kind: 'tariff-card', minHeight: 600 },
  { config: '2026/verhuur/zoek-op-accomodatie', kind: 'tile-search', minHeight: 490 },
  /**
   * The same two under a 2027 path.
   *
   * The year in a config is a NAME, not a filter — which season a visitor is
   * quoted follows from the dates they pick and the validity windows on the
   * published cards, and nothing here consults it. But the park's pages are
   * named by year, `kampeer-tarieven-2026` and `accommodatie-zoeken-2026`, and
   * a 2027 page whose iframe says 2026 is a thing somebody has to explain every
   * time they see it.
   *
   * Both spellings are kept because both will exist at once: the 2026 pages
   * stay up while the 2027 ones are built, and a redirect from one to the other
   * would be a second thing to get right for no gain.
   */
  { config: '2027/kamperen/comfort', kind: 'tariff-card', minHeight: 600 },
  { config: '2027/verhuur/zoek-op-accomodatie', kind: 'tile-search', minHeight: 490 },

  /*
    THE PER-ACCOMMODATION PAGES, which the old calculator also served.

    The park has a page per accommodation, and each links out to a calculator
    URL naming that accommodation. Only the two SEARCH configs above were
    registered, so `/2026/verhuur/trekkershut` — a URL the legacy calculator
    answers 200 to this minute — returned "Unknown calculator" after the
    cutover. A guest following the Reserveren link off the Trekkershut page got
    a 404 in an iframe.

    That is precisely the regression this bridge exists to prevent, and it was
    invisible to every check made at cutover: the two pages that were tested
    are the two that were registered.

    Only these two, and that is measured rather than assumed — the legacy site
    was probed for the whole alias table and answers 200 for exactly these. The
    `verhuur/caravan/…` and `verhuur/lodge/…` names in `KEY_ALIASES` are
    caption and data-file keys, not page URLs, and 404 on the old calculator
    too. Adding them would invent URLs the park never had.

    `keyToAccommodationId` already resolves both: `verhuur-trekkershut` and
    `kamperen-comfort-kamperen-comfort` are in the alias table. Nothing else
    was needed — only saying that the page may be asked for.
  */
  { config: '2026/verhuur/trekkershut', kind: 'tariff-card', minHeight: 600 },
  { config: '2027/verhuur/trekkershut', kind: 'tariff-card', minHeight: 600 },
  // The doubled path the old site used for camping. Same card as
  // `{year}/kamperen/comfort`, reached by the URL the CMS actually links to.
  { config: '2026/kamperen/comfort/kamperen-comfort', kind: 'tariff-card', minHeight: 600 },
  { config: '2027/kamperen/comfort/kamperen-comfort', kind: 'tariff-card', minHeight: 600 },
];

/**
 * Dutch guests, euros, De Vergarde's own clock.
 *
 * Hardcoded ON PURPOSE, and this is the one folder where that is allowed. The old
 * calculator had no language switch, the site it serves is Dutch-only, and the
 * whole bridge retires with the price calculator. Everywhere else these are park
 * settings resolved at runtime; see `docs/decisions.md`.
 *
 * `LANG` is separate from `LOCALE` because they answer different questions: the
 * widget speaks languages (`nl`) and `Intl` wants a locale (`nl-NL`). It is also
 * what gets forwarded to the guest site, so a guest clicking through from the old
 * page does not land on an English form because their browser asked for one.
 *
 * `TIME_ZONE` matters for one thing here — what "today" is. A guest opening the
 * calculator at 00:30 Amsterdam time is on a new day; UTC still calls it
 * yesterday, and the calendar would offer an arrival that has passed.
 */
export const LOCALE = 'nl-NL';
export const LANG = 'nl';
export const TIME_ZONE = 'Europe/Amsterdam';
export const CURRENCY = 'EUR';

/**
 * CMS keys whose derived id is not our accommodation id.
 *
 * The markers on the live 2026 page are the old calculator's config paths —
 *
 *     {[2026/verhuur/lodge/zomerlodge.json]}
 *     {[2026/verhuur/caravan/blauwe-caravan.json]}
 *     {[2026/verhuur/trekkershut.json]}          (no variant segment)
 *
 * — and `keyToAccommodationId` reduces each to the path after the year, joined
 * with `-`. That was the park's accommodation id, because the 2026 port derived
 * its ids from the same paths.
 *
 * It is not any more. The ids now follow the LABELS an operator maintains, so
 * that `verhuur-caravan-waterkant-chalet` labelled "Triumph 2" and
 * `kamperen-comfort-kamperen-comfort` labelled "Kamperen comfort" stopped
 * contradicting themselves. See `tools/handoff-2027.js`.
 *
 * This table is what keeps HolidayMedia's existing pages working across that
 * rename, and it is exactly the case the escape hatch was written for: their
 * captions cannot be edited before the cutover, and a marker that resolves to
 * nothing shows a tile with no price and a warning in a console nobody has
 * open. Keyed on the REDUCED id rather than the full path, so one entry covers
 * every year a marker might carry.
 *
 * It retires with the old pages: once HolidayMedia's captions say
 * `{[triumph-2]}`, every line here is dead weight.
 */
/**
 * The park's own word for a part of the estate, and ours.
 *
 * The old calculator's config paths name a category — `2027/verhuur/...`,
 * `2027/kamperen/...` — and the catalogue publishes the same distinction on
 * every card as `kind`. They are the same fact in two languages, so this is the
 * translation and nothing more; the page a visitor is on already says which
 * part of the park it is about.
 *
 * WHY IT MATTERS. A tile search was mounted UNSCOPED, on the stated ground that
 * "a tile search is about the whole park by definition". It is not: the rental
 * page's thirteen tiles are thirteen rental units, and answering for the whole
 * park put the campsite's dog in the party picker on a page that sells none —
 * reported as "for rental no dogs, so don't render it".
 *
 * Derived from the path rather than listed as ids, so adding a fourteenth
 * rental unit changes nothing here.
 */
export const KIND_BY_CATEGORY: Readonly<Record<string, string>> = {
  kamperen: 'camping',
  verhuur: 'rental',
};

/**
 * The kind a config path is about, or nothing.
 *
 * Nothing rather than a guess: an unrecognised category means the widget
 * answers for the whole park, which is what it did before this existed and is
 * the safe direction to be wrong in — a missing control is visible, a wrongly
 * hidden accommodation is not.
 */
export function kindOfConfig(config: string): string | null {
  const parts = config.split('/').filter((p) => p !== '');
  const category = /^\d{4}$/.test(parts[0] ?? '') ? parts[1] : parts[0];
  return (category && Object.hasOwn(KIND_BY_CATEGORY, category))
    ? KIND_BY_CATEGORY[category]! : null;
}

export const KEY_ALIASES: Readonly<Record<string, string>> = {
  'verhuur-caravan-blauwe-caravan': 'blauwe-caravan',
  'verhuur-caravan-groene-caravan': 'groene-caravan',
  'verhuur-caravan-oranje-caravan': 'oranje-caravan',
  'verhuur-caravan-magnolia-capelle': 'capelle-34',
  // Four legacy names, two products. The 2025 sheet prices "Tri 6, 5" as one
  // row and "Cap 21, Tr 2" as another, so the old pages' four markers land on
  // the two accommodations that actually exist.
  'verhuur-caravan-violet-triumph': 'triumph-5-6',
  'verhuur-caravan-violet-triumph5': 'triumph-5-6',
  'verhuur-caravan-waterkant-chalet': 'capelle-21-triumph-2',
  'verhuur-caravan-violet-capelle': 'capelle-21-triumph-2',
  'verhuur-caravan-waterplas-chalet': 'brederode-50',
  'verhuur-lodge-parklodge-vier-personen': 'parklodge-4',
  'verhuur-lodge-parklodge-vijf-personen': 'parklodge-5',
  'verhuur-lodge-zomerlodge': 'zomerlodge',
  'verhuur-trekkershut': 'trekkershut',
  'kamperen-comfort-kamperen-comfort': 'kamperen-comfort',
};
